/** tm/legal/useLegalHubData.ts — 주제 목록·수집 상태 로딩, 「지금 수집」 실행 후 끝날 때까지 상태 폴링 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getTopics, getSummary, getNewsStatus, getBillsStatus, runNow } from './legalApi';
import { errMsg } from './legalTheme';
import type { AlertTab, BillsStatus, LegalSummary, Topic } from './legalTypes';

const RUN_POLL_MS = 5000;

export interface HubData {
  topics: Topic[]; topicsLoading: boolean; topicsError: string; summary: LegalSummary | null; running: boolean;
  refreshTopics: () => Promise<void>; refreshSummary: () => Promise<LegalSummary | null>;
  run: () => Promise<{ ok: boolean; message: string }>;
  /** 읽음 처리 직후 주제별 알림 수를 낙관적으로 줄인다 */
  decAlerts: (labels: string[], tab: AlertTab) => void;
  bills: BillsStatus | null;
  decNews: (labels: string[]) => void;
}

export function useLegalHubData(onRunDone: (s: LegalSummary | null) => void): HubData {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [topicsLoading, setTL] = useState(true);
  const [topicsError, setTE] = useState('');
  const [summary, setSummary] = useState<LegalSummary | null>(null);
  const [polling, setPolling] = useState(false);
  const [bills, setBills] = useState<BillsStatus | null>(null);
  const doneRef = useRef(onRunDone);
  doneRef.current = onRunDone;

  const refreshTopics = useCallback(async () => {
    try { setTopics(await getTopics()); setTE(''); }
    catch (e) { setTE(errMsg(e, '주제를 불러오지 못했습니다')); }
    finally { setTL(false); }
  }, []);
  const refreshSummary = useCallback(async () => {
    try {
      const [s, n] = await Promise.all([getSummary(), getNewsStatus().catch(() => null)]);
      // 뉴스 상태는 /news/status 로 따로 온다 → summary 에 합친다 (summary 에 이미 있으면 그대로)
      const merged: LegalSummary = n ? {
        ...s, newsProviders: s.newsProviders || n.providers,
        news: s.news || { enabled: n.enabled !== false, lastRun: n.lastRun ? { finished_at: n.lastRun.finished_at, status: n.lastRun.status } : null },
      } : s;
      setSummary(merged); return merged;
    } catch { return null; }
  }, []);
  useEffect(() => { refreshTopics(); refreshSummary(); getBillsStatus().then(setBills, () => setBills(null)); }, [refreshTopics, refreshSummary]);

  const running = polling || !!summary?.running;
  // 수집 중이면 끝날 때까지 5초마다 상태 확인 → 끝나면 콜백
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      const s = await refreshSummary();
      if (s && !s.running) { setPolling(false); doneRef.current(s); }
    }, RUN_POLL_MS);
    return () => clearInterval(t);
  }, [running, refreshSummary]);

  const run = useCallback(async () => {
    try { await runNow(); setPolling(true); return { ok: true, message: '일괄 수집을 시작했습니다. 몇 분 뒤 끝나면 목록을 새로 고칩니다' }; }
    catch (e) {
      const m = errMsg(e, '수집을 시작하지 못했습니다');
      if (/수집 중/.test(m)) { setPolling(true); return { ok: true, message: '이미 수집 중입니다. 끝나면 목록이 새로 고쳐집니다' }; }
      return { ok: false, message: m };
    }
  }, []);

  const decAlerts = useCallback((labels: string[], tab: AlertTab) => {
    setTopics(prev => prev.map(t => (labels.includes(t.label) && t.alerts > 0 && t.alert !== false ? {
      ...t, alerts: t.alerts - 1,
      alertsByTab: t.alertsByTab ? { ...t.alertsByTab, [tab]: Math.max(0, (t.alertsByTab[tab] || 0) - 1) } : t.alertsByTab,
    } : t)));
  }, []);
  const decNews = useCallback((labels: string[]) => {
    setTopics(prev => prev.map(t => (labels.includes(t.label) && t.newsNew > 0 ? { ...t, newsNew: t.newsNew - 1 } : t)));
  }, []);

  return { topics, topicsLoading, topicsError, summary, running, refreshTopics, refreshSummary, run, decAlerts, decNews, bills };
}
